import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RadioButtonChecked,
  RadioButtonUnchecked,
} from 'styled-icons/material';
import { Globe, Mail, UserCircle } from 'styled-icons/heroicons-outline';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Menu from '../common/components/Menu';
import Dropdown from '../common/components/Dropdown';
import Avatar from '../common/components/Avatar';
import { useTheme } from '../common/components/ThemeProvider';
import { callMethod } from '../common/utilsClient/ddp/actions';
import {
  setOptions,
  apiCurrentUserNotificationLanguages,
} from '../common/api/currentUser';
import { notifyError, notifySuccess } from '../utils/notify';
import useLanguageSelector from '../utils/useLanguageSelector';
import {
  notificationLanguageOptionsFromCodes,
  DEFAULT_NOTIFICATION_LANGUAGE,
} from '../utils/notificationLanguageOptions';
import CurrentUser from '../models/CurrentUser';

const UserAvatar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(CurrentUser.select.user());
  const theme = useTheme();
  const { languageOptions, currentLanguage, onLanguageChange } =
    useLanguageSelector();

  // TECH-2730 RA10. Two language menus, deliberately, because they answer two
  // different questions. "Language" above is the portal's DISPLAY language:
  // one of the seven UI bundles, kept in this browser, never seen by the
  // server. "Notification language" is what the user's ACCOUNT EMAILS are
  // written in: one of the organisation's message languages, stored on the
  // user, read by the server at send time. Before this, staff had no such
  // field and every account email went out in English (QA RA10, 29 Sep).
  //
  // The list comes from the SERVER, from the same organisation chain the
  // send path walks (any group type -> nearest organisation -> every
  // ancestor). Computing it here from the user's own organisation groups
  // offered a Project/Ward member nothing and nobody their parents'
  // languages, which are not published to non-members (code review,
  // 2026-10-04). Fetched on mount and again whenever the menu opens, so a
  // language an administrator just added appears without a reload.
  const [notificationCodes, setNotificationCodes] = useState([]);
  const loadNotificationLanguages = useCallback(
    () =>
      dispatch(callMethod(apiCurrentUserNotificationLanguages, {}))
        .then((result) => {
          setNotificationCodes((result && result.languages) || []);
          return null;
        })
        // A failed fetch leaves the default option, which is always valid.
        .catch(() => null),
    [dispatch],
  );
  useEffect(() => {
    loadNotificationLanguages();
  }, [loadNotificationLanguages]);
  const notificationOptions = useMemo(
    () => notificationLanguageOptionsFromCodes(notificationCodes, t),
    [notificationCodes, t],
  );
  const currentNotificationLanguage =
    currentUser?.notificationLanguage || DEFAULT_NOTIFICATION_LANGUAGE;
  const onNotificationLanguageChange = useCallback(
    (value) =>
      dispatch(callMethod(setOptions, { notificationLanguage: value }))
        .then(() => {
          notifySuccess(t('forms:notificationLanguage.saved'))();
          return null;
        })
        .catch(notifyError()),
    [dispatch, t],
  );

  const handleSelectLightMode = () => theme.toggle(false);
  const handleSelectDarkMode = () => theme.toggle(true);

  // One sub-menu open at a time. antd's Menu lets several hover-opened
  // sub-menus stay open together, and with two language menus side by side
  // their popups overlapped (Jubayer, 2026-10-02). Controlled openKeys, last
  // one wins.
  const [openKeys, setOpenKeys] = useState([]);
  const handleOpenChange = useCallback(
    (keys) => setOpenKeys(keys.length ? [keys[keys.length - 1]] : []),
    [],
  );

  return (
    <Dropdown
      overlay={
        <Menu openKeys={openKeys} onOpenChange={handleOpenChange}>
          <Menu.Item icon={<UserCircle size="0.95em" />}>
            <Link to="/profile">{t('myProfile')}</Link>
          </Menu.Item>
          <Menu.Divider />
          <Menu.SubMenu
            key="display-language"
            icon={<Globe size="1.25em" />}
            title={t('forms:displayLanguage.label')}
          >
            {languageOptions.map(({ value, label }) => (
              <Menu.Item
                key={value}
                icon={
                  value === currentLanguage ? (
                    <RadioButtonChecked size="1em" />
                  ) : (
                    <RadioButtonUnchecked size="1em" />
                  )
                }
                onClick={() => onLanguageChange(value)}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.SubMenu>
          <Menu.SubMenu
            key="notification-language"
            data-testid="menu-notification-language"
            icon={<Mail size="1.25em" />}
            title={t('forms:notificationLanguage.label')}
          >
            {notificationOptions.map(({ value, label }) => (
              <Menu.Item
                key={value || 'default'}
                data-testid={`menu-notification-language-${value || 'default'}`}
                icon={
                  value === currentNotificationLanguage ? (
                    <RadioButtonChecked size="1em" />
                  ) : (
                    <RadioButtonUnchecked size="1em" />
                  )
                }
                onClick={() => onNotificationLanguageChange(value)}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.SubMenu>
          <Menu.Divider />
          <Menu.Item
            icon={
              theme.mode === 'light' ? (
                <RadioButtonChecked size="1em" />
              ) : (
                <RadioButtonUnchecked size="1em" />
              )
            }
            onClick={handleSelectLightMode}
          >
            {t('forms:lightTheme.label')}
          </Menu.Item>
          <Menu.Item
            icon={
              theme.mode === 'dark' ? (
                <RadioButtonChecked size="1em" />
              ) : (
                <RadioButtonUnchecked size="1em" />
              )
            }
            onClick={handleSelectDarkMode}
          >
            {t('forms:darkTheme.label')}
          </Menu.Item>
        </Menu>
      }
      trigger={['click']}
      onVisibleChange={(visible) => {
        if (visible) loadNotificationLanguages();
      }}
    >
      <button type="button">
        <Avatar
          kind="bold"
          src={currentUser?.getPicture()}
          alt={currentUser?.getFullName()}
        >
          {currentUser?.getInitials()}
        </Avatar>
      </button>
    </Dropdown>
  );
};

export default UserAvatar;
